/**
 * Pokémon names, types and sprites, from the data snapshot for a league's mod (built from OgreClan's Showdown
 * server by @ogreclan/pokemon-core), so a Champions league shows Champions' Pokémon.
 */
import { createData, type PokemonData, type Species } from '@ogreclan/pokemon-core';
import { setSpeciesNames, speciesNamesFromData } from '@ogreclan/league-core';
import { useEffect, useState } from 'preact/hooks';

const SPRITES = 'https://play.pokemonshowdown.com/sprites/gen5/';
const ANIMATED = 'https://play.pokemonshowdown.com/sprites/ani/';
const cache = new Map<string, Promise<PokemonData>>();

export function loadPokemon(mod = 'gen9'): Promise<PokemonData> {
	let promise = cache.get(mod);
	if (!promise) {
		promise = fetch(`/data/${mod}.json`)
			.then(response => {
				if (response.ok) return response.json();
				if (mod !== 'gen9') return loadPokemon('gen9');
				throw new Error(`Couldn't load the Pokémon data.`);
			})
			.then(snapshot => 'getSpecies' in snapshot ? snapshot as PokemonData : createData(snapshot));
		cache.set(mod, promise);
	}
	return promise;
}

/** The Pokémon data for a mod, once it's loaded; league-core's names come from it too */
export function usePokemon(mod: string | undefined) {
	const [data, setData] = useState<PokemonData | null>(null);
	useEffect(() => {
		if (!mod) return;
		let current = true;
		void loadPokemon(mod).then(loaded => {
			if (!current) return;
			setSpeciesNames(speciesNamesFromData(loaded));
			setData(loaded);
		});
		return () => {
			current = false;
		};
	}, [mod]);
	return data;
}

/** "Mega Charizard Y" for an id the data doesn't know: the id, tidied */
function fallbackName(id: string) {
	return id.charAt(0).toUpperCase() + id.slice(1);
}

export function speciesOf(data: PokemonData | null, id: string): Species | null {
	return data?.getSpecies(id) || null;
}

export function nameOf(data: PokemonData | null, id: string) {
	return speciesOf(data, id)?.name || fallbackName(id);
}

/**
 * A Pokémon's sprite: Showdown's still one, or its animated one when it has no still one (Champions' new Megas,
 * like Raichu-Mega-Y, have only that so far), or else its base forme's; hidden if there's none at all
 */
export function Sprite({ data, species }: { data: PokemonData | null, species: Species }) {
	const tries = [`${SPRITES}${species.sprite}.png`, `${ANIMATED}${species.sprite}.gif`];
	const base = speciesOf(data, species.baseSpecies);
	if (base && base.sprite !== species.sprite) tries.push(`${SPRITES}${base.sprite}.png`);
	return <img src={tries[0]} alt="" loading="lazy" onError={event => {
		const img = event.currentTarget as HTMLImageElement;
		const next = tries[Number(img.dataset.tried || 0) + 1];
		img.dataset.tried = String(Number(img.dataset.tried || 0) + 1);
		if (next) img.src = next;
		else img.style.visibility = 'hidden';
	}} />;
}

export function Types({ types }: { types: readonly string[] }) {
	return <span class="types">{types.map(type => <span key={type} class={`type type-${type}`}>{type}</span>)}</span>;
}

/** A Pokémon: its sprite and name (and types, if asked: beside the name, or under it if `stacked`) */
export function Mon({ data, id, types = false, stacked = false }: { data: PokemonData | null, id: string, types?: boolean, stacked?: boolean }) {
	const species = speciesOf(data, id);
	return <span class={`mon${stacked ? ' stacked' : ''}`}>
		{species && <Sprite data={data} species={species} />}
		{stacked ? <span class="text">
			<span class="name">{species?.name || fallbackName(id)}</span>
			{types && species && <Types types={species.types} />}
		</span> : <>
			<span class="name">{species?.name || fallbackName(id)}</span>
			{types && species && <Types types={species.types} />}
		</>}
	</span>;
}
